import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { replaceEqualDeep, useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { fetchGeneration, fetchGenerations, fetchGenerationsByIds, GenerationNotFoundError } from '../api/generationsApi';
import {
  findGenerationSummary,
  isActiveSummary,
  isStaleSummary,
  listGenerationSummaries,
  missingFromIdsAnswer,
  reconcilePages,
  removeFromPages,
  sameSummary,
  summaryAsOf,
  summaryChanged,
  trimToFirstPage,
  upsertIntoPages,
  type GenerationsData,
} from '../lib/cache';
import { markItemRetrying, summaryAfterRetry, summaryOfDetail } from '../lib/retry';
import { hasActiveWatched, hasWatchedInVisit } from '../lib/watchModel';
import { refreshGenerationDetail } from './detailRefresh';
import { generationKeys } from './keys';
import { knownSummaries } from './knownSummaries';
import type { GenerationDetail, GenerationSummary, GenerationsPage } from './types';
import { useGenerationWatch } from './watch';

/** Страница списка — 10 записей: видно 5, следующие 5 уже загружены (ТЗ главной § 22.7). */
export const GENERATIONS_PAGE_SIZE = 10;

/**
 * Подписка `useSyncExternalStore` на кэш запросов. Кэш шлёт события и синхронно во время рендера другого
 * компонента (его новый `useQuery` — «observerAdded»); немедленный `onChange` у родителя, чей снимок в
 * этом проходе уже другой, React встречает предупреждением «Cannot update a component while rendering a
 * different component». Поэтому уведомление откладывается до микрозадачи — снимок React всё равно сверит.
 */
function subscribeCacheDeferred(qc: QueryClient, onChange: () => void): () => void {
  let queued = false;
  let alive = true;
  const off = qc.getQueryCache().subscribe(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      if (alive) onChange();
    });
  });
  return () => {
    alive = false;
    off();
  };
}
/** Запасной опрос первой страницы, пока что-то генерируется и вкладка видна (§ 22.9). */
export const GENERATIONS_POLL_MS = 10_000;
/** Сколько идущих записей вне первой страницы опрашиваем за один шаг (`?ids=` принимает до 50). */
const OUTSIDE_POLL_LIMIT = 20;

/** Страница ответа с меткой, когда ушёл запрос: по ней сводки сверяются с пришедшими за это время. */
async function fetchStampedPage(cursor: string | null, signal?: AbortSignal): Promise<GenerationsPage> {
  const requestedAt = Date.now();
  const page = await fetchGenerations({ cursor, limit: GENERATIONS_PAGE_SIZE, signal });
  return { ...page, requestedAt };
}

/**
 * Бесконечный список «Генераций», новые сверху. Без `refetchInterval`: в React Query 5 он
 * перечитывал бы подряд все загруженные страницы — для опроса есть useGenerationsHeadQuery.
 * Уходя с главной, обрезаем кэш до первой страницы: при возврате окно снова пять записей,
 * и перечитывается одна страница, а не все. Ответ, посчитанный до SSE, свежую сводку не
 * затирает: сверка — в момент записи в кэш (`structuralSharing`).
 */
export function useGenerationsQuery(options: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  useEffect(() => () => trimGenerationsCache(qc), [qc]);
  return useInfiniteQuery({
    queryKey: generationKeys.list(),
    queryFn: ({ pageParam, signal }) => fetchStampedPage(pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: GenerationsPage) => last.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
    staleTime: 0,
    structuralSharing: (previous: unknown, next: unknown) =>
      replaceEqualDeep(previous, reconcilePages(next as GenerationsData, knownSummaries)),
  });
}

/**
 * Уход с главной: кэш списка — до первой страницы. Если в этот момент догружается страница («Показать
 * ещё») или перечитываются все загруженные, запрос отменяем: иначе он допишет в кэш все прежние страницы,
 * и при возврате бесконечный запрос перечитает их подряд. Отмена возвращает прежнее состояние — обрезаем
 * после неё. Первую загрузку не трогаем (StrictMode снимает и ставит эффект сразу).
 */
export function trimGenerationsCache(qc: QueryClient): void {
  const trim = () => {
    const data = qc.getQueryData<GenerationsData>(generationKeys.list());
    const next = trimToFirstPage(data);
    if (next !== data) qc.setQueryData<GenerationsData>(generationKeys.list(), next);
  };
  const query = qc.getQueryCache().find({ queryKey: generationKeys.list(), exact: true });
  const loadedPages = (query?.state.data as GenerationsData | undefined)?.pages.length ?? 0;
  const fetchMore = (query?.state.fetchMeta as { fetchMore?: unknown } | null | undefined)?.fetchMore;
  if (query?.state.fetchStatus === 'fetching' && (fetchMore || loadedPages > 1)) {
    void qc.cancelQueries({ queryKey: generationKeys.list(), exact: true }).then(trim);
    return;
  }
  trim();
}

/**
 * Состав записи (`GET /generations/{id}`) — и сразу её сводка в список и слежение: иначе раскрытие показало бы
 * свежее тело под старым заголовком, а запись вне первой страницы (SSE по записям старше недели сервер не
 * шлёт) навсегда осталась бы «генерируем». Сводка верна на момент запроса; посчитанная раньше SSE — не пишется.
 */
async function loadGenerationDetail(qc: QueryClient, id: string, signal?: AbortSignal): Promise<GenerationDetail> {
  const requestedAt = Date.now();
  const detail = await fetchGeneration(id, signal);
  upsertGenerationSummary(qc, summaryOfDetail(detail), requestedAt, { detail: false });
  return detail;
}

/**
 * Перечитать состав записи сейчас, мимо троттлинга: опрос записей вне первой страницы, проверка после
 * «Добавить вручную». Деталь и сводка — в кэш. Записи больше нет (404, модуль удалён): раскрытая покажет
 * это сама и уйдёт через 3 с, свёрнутая — убирается сразу.
 */
export async function refetchGenerationNow(qc: QueryClient, id: string): Promise<void> {
  try {
    await qc.fetchQuery({
      queryKey: generationKeys.byId(id),
      queryFn: ({ signal }) => loadGenerationDetail(qc, id, signal),
      staleTime: 0,
    });
  } catch (err) {
    if (!(err instanceof GenerationNotFoundError)) return;
    const query = qc.getQueryCache().find({ queryKey: generationKeys.byId(id), exact: true });
    if (!query || query.getObserversCount() === 0) removeGeneration(qc, id);
  }
}

/** Идущие записи: загруженные в список и нажатия этого захода (их сводка — из списка или запасная). */
function activeGenerationIds(qc: QueryClient): string[] {
  const ids = new Set<string>();
  for (const summary of listGenerationSummaries(qc.getQueryData<GenerationsData>(generationKeys.list()))) {
    if (isActiveSummary(summary)) ids.add(summary.id);
  }
  const watch = useGenerationWatch.getState();
  for (const entry of Object.values(watch.entries)) {
    if (entry.visit !== watch.visit) continue;
    const summary = readGenerationSummary(qc, entry.id);
    if (summary && isActiveSummary(summary)) ids.add(entry.id);
  }
  return [...ids];
}

/**
 * Идущие записи, которых нет в первой странице (за пятёркой, «Повторить» в старой записи): опрос первой
 * страницы их не видит. Их сводки — тем же 10-секундным шагом одним запросом `GET /generations?ids=…`
 * (контракт § 8, «Запасной путь»). Записи, которых в ответе нет, ученик больше не видит (модуль удалён) —
 * раскрытая покажет это сама (её деталь перечитывается и ответит 404), остальные убираются.
 */
async function pollOutsideHead(qc: QueryClient, head: GenerationsPage): Promise<void> {
  const inHead = new Set(head.items.map((summary) => summary.id));
  const ids = activeGenerationIds(qc)
    .filter((id) => !inHead.has(id))
    .slice(0, OUTSIDE_POLL_LIMIT);
  if (ids.length === 0) return;
  const requestedAt = Date.now();
  let answer: GenerationsPage;
  try {
    answer = await fetchGenerationsByIds(ids);
  } catch {
    return; // Нет связи — следующий шаг опроса.
  }
  for (const summary of answer.items) {
    if (upsertGenerationSummary(qc, summary, requestedAt).changed) refreshGenerationDetail(qc, summary.id);
  }
  for (const id of missingFromIdsAnswer(ids, answer.items) ?? []) {
    const detail = qc.getQueryCache().find({ queryKey: generationKeys.byId(id), exact: true });
    if (detail && detail.getObserversCount() > 0) refreshGenerationDetail(qc, id);
    else removeGeneration(qc, id);
  }
}

/**
 * Запасной опрос: отдельный лёгкий запрос первой страницы раз в 10 с; ответ вливается в список сразу, в
 * самом запросе (`upsertGenerationSummary` с моментом запроса), а не эффектом от данных: закэшированный
 * ответ прошлого захода при возврате на главную ничего не перезаписывает. Пока вкладка скрыта, React Query
 * не опрашивает (`refetchIntervalInBackground: false`). Первое значение — первая страница списка, поэтому
 * сразу после загрузки списка второй запрос не уходит. Если список не загрузился, опрос заодно и повтор
 * его загрузки. Идущие записи вне первой страницы — по детали (`pollOutsideHead`).
 */
export function useGenerationsHeadQuery(enabled: boolean) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: generationKeys.head(),
    queryFn: async ({ signal }) => {
      const page = await fetchStampedPage(null, signal);
      mergeGenerationsHead(qc, page);
      void pollOutsideHead(qc, page);
      return page;
    },
    enabled,
    refetchInterval: enabled ? GENERATIONS_POLL_MS : false,
    refetchIntervalInBackground: false,
    staleTime: GENERATIONS_POLL_MS,
    initialData: () => qc.getQueryData<GenerationsData>(generationKeys.list())?.pages[0],
    initialDataUpdatedAt: () => qc.getQueryState(generationKeys.list())?.dataUpdatedAt,
  });
}

/** Есть ли идущая генерация среди загруженных записей или нажатий этого захода. */
export function hasActiveGenerations(qc: QueryClient): boolean {
  return (
    listGenerationSummaries(qc.getQueryData<GenerationsData>(generationKeys.list())).some(isActiveSummary) ||
    hasActiveWatched(useGenerationWatch.getState())
  );
}

/**
 * Запасной опрос на главной: включается, пока среди записей или нажатий этого захода есть
 * идущая генерация. Монтирует его секция «Генерации» — она живёт только на главной.
 */
export function useGenerationsPolling() {
  const qc = useQueryClient();
  const subscribe = useCallback((onChange: () => void) => {
    const offCache = subscribeCacheDeferred(qc, onChange);
    const offWatch = useGenerationWatch.subscribe(onChange);
    return () => {
      offCache();
      offWatch();
    };
  }, [qc]);
  const active = useSyncExternalStore(subscribe, () => hasActiveGenerations(qc));
  return useGenerationsHeadQuery(active);
}

/** Влить первую страницу (опрос, перечитка): новые записи — по порядку, известные — заменить, если ответ
 *  не старше уже известной сводки (момент — `requestedAt` страницы). */
export function mergeGenerationsHead(qc: QueryClient, page: GenerationsPage): void {
  if (!qc.getQueryData<GenerationsData>(generationKeys.list())) {
    qc.setQueryData<GenerationsData>(generationKeys.list(), { pages: [page], pageParams: [null] });
    return;
  }
  const asOf = page.requestedAt ?? Date.now();
  // С конца: каждая следующая новая запись новее предыдущей и встаёт над ней.
  for (const summary of [...page.items].reverse()) {
    if (upsertGenerationSummary(qc, summary, asOf).changed) refreshGenerationDetail(qc, summary.id);
  }
}

/** Деталь записи — при первом раскрытии. 404 не повторяем: запись ушла. */
export function useGenerationQuery(id: string | null, enabled = true) {
  const qc = useQueryClient();
  return useQuery<GenerationDetail, Error>({
    queryKey: generationKeys.byId(id ?? ''),
    queryFn: ({ signal }) => loadGenerationDetail(qc, id!, signal),
    enabled: enabled && !!id,
    staleTime: 0,
    retry: (count, error) => !(error instanceof GenerationNotFoundError) && count < 1,
  });
}

/**
 * Сводка записи для сигнала у строки: пассивная подписка на кэш списка, без своего опроса и
 * без перечитки при монтировании; пока записи в списке нет — запасная сводка из слежения.
 */
export function useGenerationSummary(id: string | null): GenerationSummary | null {
  const qc = useQueryClient();
  const fallback = useGenerationWatch((s) => (id ? (s.entries[id]?.summary ?? null) : null));
  const subscribe = useCallback((onChange: () => void) => subscribeCacheDeferred(qc, onChange), [qc]);
  const fromList = useSyncExternalStore(subscribe, () =>
    id ? findGenerationSummary(qc.getQueryData<GenerationsData>(generationKeys.list()), id) : null,
  );
  return fromList ?? fallback;
}

export interface UpsertGenerationResult {
  /** Сменились статус, счётчики или жетоны: открытую деталь пора перечитать. */
  changed: boolean;
  /** Запись есть в загруженном списке (была или только что встала). */
  inList: boolean;
}

/**
 * Свежая сводка записи (ответ «Создать», SSE `generationUpdated`, опрос, деталь) — во все страницы
 * списка, в деталь и в запасную сводку слежения. Записи нет — встаёт по порядку (`upsertIntoPages`).
 * Момент, на который сводка верна, — её `computedAt` (контракт § 7.2), а у старого бэкенда — `asOf` по часам
 * устройства (по умолчанию — сейчас). Если о записи уже известно более позднее состояние, сводка устарела и
 * не пишется: ответ, посчитанный до SSE «готово», не откатывает запись. Та же по содержимому — кэш и слежение
 * не трогаем: подписчики не просыпаются на каждое событие.
 */
export function upsertGenerationSummary(
  qc: QueryClient,
  summary: GenerationSummary,
  asOf: number = Date.now(),
  options: { detail?: boolean } = {},
): UpsertGenerationResult {
  const listData = qc.getQueryData<GenerationsData>(generationKeys.list());
  const at = summaryAsOf(summary, asOf);
  if (isStaleSummary(knownSummaries.get(summary.id), at)) {
    return { changed: false, inList: findGenerationSummary(listData, summary.id) !== null };
  }
  knownSummaries.set(summary.id, { summary, asOf: at });
  // Прежняя сводка — из списка, иначе из детали, иначе из слежения.
  let previous: GenerationSummary | null = useGenerationWatch.getState().entries[summary.id]?.summary ?? null;
  if (options.detail !== false) {
    const detail = qc.getQueryData<GenerationDetail>(generationKeys.byId(summary.id));
    if (detail) {
      const own = summaryOfDetail(detail);
      previous = own;
      if (!sameSummary(own, summary)) {
        qc.setQueryData<GenerationDetail>(generationKeys.byId(summary.id), { ...detail, ...summary, items: detail.items });
      }
    }
  }
  const result = upsertIntoPages(listData, summary);
  if (result.previous) previous = result.previous;
  if (result.data !== listData) qc.setQueryData<GenerationsData>(generationKeys.list(), result.data);
  useGenerationWatch.getState().updateSummary(summary);
  return { changed: summaryChanged(previous, summary), inList: result.inserted || result.previous !== null };
}

/**
 * После «Создать» и «Добавить» на главной: список загружен — сводка встаёт в кэш; иначе список
 * перечитывается и придёт уже с новой записью (сервер пишет её до ответа). На `/cards/new` и
 * `/cards/topics` достаточно invalidateQueries(generationKeys.list()). `true` — запись есть в
 * загруженном списке (сигнал у строки решает от вставки её строки); `false` — вставлять некуда,
 * сигнал решает от ответа (ТЗ § 8). `asOf` — когда ушёл запрос: SSE о той же записи, пришедшее
 * раньше ответа, может быть свежее его.
 */
export function addCreatedGeneration(qc: QueryClient, summary: GenerationSummary, asOf?: number): boolean {
  if (qc.getQueryData(generationKeys.list()) && upsertGenerationSummary(qc, summary, asOf).inList) return true;
  void qc.invalidateQueries({ queryKey: generationKeys.list() });
  return false;
}

/** Сводка записи для сигнала у строки: из кэша списка, а пока записи там нет — запасная из слежения. */
export function readGenerationSummary(qc: QueryClient, id: string): GenerationSummary | null {
  return (
    findGenerationSummary(qc.getQueryData<GenerationsData>(generationKeys.list()), id) ??
    useGenerationWatch.getState().entries[id]?.summary ??
    null
  );
}

/** Записи больше нет (404, модуль удалён): из списка, детали и слежения. */
export function removeGeneration(qc: QueryClient, id: string): void {
  qc.setQueryData<GenerationsData>(generationKeys.list(), (data) => removeFromPages(data, id));
  qc.removeQueries({ queryKey: generationKeys.byId(id), exact: true });
  knownSummaries.delete(id);
  useGenerationWatch.getState().forget(id);
}

/**
 * «Повторить · 1 жетон» запущен (ТЗ § 22.6, § 23.4 п. 2): элемент сразу «генерируется», запись —
 * «генерируем» (в детали, списке и слежении), слежение начинает новую попытку — отсчёт «дольше
 * обычного» от этого нажатия, крестик и принятие сброшены. Сводки берём из ответа повтора
 * (`generations`, контракт § 7.1: все записи с этой карточкой); старый бэкенд их не отдаёт (`null`) —
 * тогда «генерируем» рассчитывается, а настоящую сводку принесут SSE и опрос.
 */
export function applyRetryStarted(
  qc: QueryClient,
  generationId: string,
  itemId: string,
  cardId: string,
  generations: readonly GenerationSummary[] | null = null,
): void {
  const detail = qc.getQueryData<GenerationDetail>(generationKeys.byId(generationId));
  if (detail) qc.setQueryData<GenerationDetail>(generationKeys.byId(generationId), markItemRetrying(detail, itemId));
  const fromServer = generations?.find((summary) => summary.id === generationId) ?? null;
  for (const summary of generations ?? []) {
    // Шапка детали — из сводки, элементы — расчётом выше; настоящий состав перечитается по смене сводки.
    if (upsertGenerationSummary(qc, summary).changed) refreshGenerationDetail(qc, summary.id);
  }
  let next = fromServer ? (readGenerationSummary(qc, generationId) ?? fromServer) : null;
  if (!next) {
    const known =
      findGenerationSummary(qc.getQueryData<GenerationsData>(generationKeys.list()), generationId) ??
      (detail ? summaryOfDetail(detail) : null) ??
      useGenerationWatch.getState().entries[generationId]?.summary ??
      null;
    if (!known) return;
    next = summaryAfterRetry(known);
    upsertGenerationSummary(qc, next);
  }
  useGenerationWatch.getState().watch(next, [cardId], { restart: true });
}

/**
 * Записи, где ручная карточка сняла ожидание своих упавших карточек того же слова (ответ `POST /cards/manual`,
 * `released`, контракт § 7.1): их элементы теперь «удалена». Раскрытый состав перечитывается.
 */
export function applyReleasedGenerations(qc: QueryClient, released: readonly GenerationSummary[]): void {
  for (const summary of released) {
    if (upsertGenerationSummary(qc, summary).changed) refreshGenerationDetail(qc, summary.id);
  }
}

/**
 * Исход «Повторить» был неизвестен (нет ответа, 5xx), а перечитанный состав показал, что элемент
 * генерируется, — это успех: деталь и сводка — из ответа, слежение — новая попытка. `asOf` — когда ушёл
 * запрос состава.
 */
export function applyRetryConfirmed(qc: QueryClient, detail: GenerationDetail, cardId: string, asOf: number): void {
  qc.setQueryData<GenerationDetail>(generationKeys.byId(detail.id), detail);
  const summary = summaryOfDetail(detail);
  upsertGenerationSummary(qc, summary, asOf, { detail: false });
  useGenerationWatch.getState().watch(readGenerationSummary(qc, detail.id) ?? summary, [cardId], { restart: true });
}

/** Перечитанный состав после неудачного «Повторить»: деталь и сводка — из ответа (`asOf` — момент запроса). */
export function applyGenerationDetail(qc: QueryClient, detail: GenerationDetail, asOf: number): void {
  qc.setQueryData<GenerationDetail>(generationKeys.byId(detail.id), detail);
  upsertGenerationSummary(qc, summaryOfDetail(detail), asOf, { detail: false });
}

/**
 * Ручная карточка поверх упавшей (ТЗ § 22.6, § 23.1): старая запись получает «удалена» через SSE
 * `generationUpdated`. Без SSE (и у записей старше недели, по которым сервер его не шлёт) — один раз
 * перечитать её состав (вместе со сводкой) и первую страницу списка.
 */
export async function refreshGenerationSoon(qc: QueryClient, id: string): Promise<void> {
  void refetchGenerationNow(qc, id);
  try {
    mergeGenerationsHead(qc, await fetchStampedPage(null));
  } catch {
    // Нет связи — список перечитается при следующем заходе или по SSE.
  }
}

/**
 * Есть ли на главной записи «Недавно добавленных» или нажатия этого захода (ТЗ § 5, § 23.4 п. 4):
 * с ними под колонкой постоянный отступ 52 px под сигнал. Страница берёт признак отсюда, а не из
 * виджетов — виджеты страницы друг о друге не знают.
 */
export function useGenerationsPresence(): boolean {
  const qc = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => {
      const offCache = subscribeCacheDeferred(qc, onChange);
      const offWatch = useGenerationWatch.subscribe(onChange);
      return () => {
        offCache();
        offWatch();
      };
    },
    [qc],
  );
  return useSyncExternalStore(subscribe, () => {
    if (listGenerationSummaries(qc.getQueryData<GenerationsData>(generationKeys.list())).length > 0) return true;
    // Нажатия считаются только после входа на главную: в первом рендере нового захода номер захода ещё
    // прежний, и нажатия прошлого захода сошли бы за нынешние.
    const watch = useGenerationWatch.getState();
    return watch.homeVisible && hasWatchedInVisit(watch);
  });
}
